<script setup lang="ts">
import { computed } from 'vue';

import { useRecipeStore } from '@apps/main/new-desktop/core/stores/recipe/recipeStore';
import { useItineraryStore } from '@apps/main/new-desktop/core/stores';
import { i18n } from '@apps/main/modules/localization/localizationSetup';

import { AppHorizontalSelector, AppSvgIcon } from '@libs/ui';
import type { AppHorizontalSelectorItem } from '@libs/ui';
import { DateFormat, SemboDate } from '@libs/sembo-dates';

import { useActivityDetailsTicketsStore } from '../../stores/activityDetailsTicketsStore';
import { ActivityDetailsTemplateId } from '../../models';
import { useActivityDetailsStore } from '../../stores';

import ActivityDetailsFooter from './ActivityDetailsFooter.vue';
import Ticket from './Ticket.vue';

const activityDetailsTicketsStore = useActivityDetailsTicketsStore();
const recipeStore = useRecipeStore();
const activityDetailsStore = useActivityDetailsStore();
const itineraryStore = useItineraryStore();
const { t } = i18n.global;

interface TicketsSelectorDesktopEmits {
  (event: 'close'): void;
  (event: 'select-tickets'): void;
}
const emit = defineEmits<TicketsSelectorDesktopEmits>();

const bookableDaysItems = computed<AppHorizontalSelectorItem[]>(
  () =>
    activityDetailsTicketsStore.bookableDays?.map((date, index) => ({
      id: date.toISOString(),
      label:
        itineraryStore.userSelectedItinerary === undefined
          ? `${t('planner.days')}  ${index + 1}`
          : new SemboDate(date).format(DateFormat.DateWithFullMonthComma),
      disabled: !activityDetailsTicketsStore.thereIsTicketAvailability(index),
    })) ?? []
);
</script>

<template>
  <div class="tickets-selector-desktop">
    <div class="modal-content">
      <div class="activities-dates-wrapper">
        <AppHorizontalSelector
          v-if="
            activityDetailsTicketsStore.bookableDays &&
            activityDetailsTicketsStore.bookableDays.length > 1
          "
          :items="bookableDaysItems"
          :selected="
            activityDetailsTicketsStore.selectedDate?.get().toISOString() ?? ''
          "
          @select="activityDetailsTicketsStore.selectDate(new Date($event))"
        />
        <div
          v-else-if="
            activityDetailsTicketsStore.bookableDays &&
            activityDetailsTicketsStore.bookableDays.length === 1
          "
          class="single-date"
        >
          <AppSvgIcon name="calendarOutlined" size="24" />
          <span>
            {{
              new SemboDate(activityDetailsTicketsStore.bookableDays[0]).format(
                DateFormat.DateWithFullMonthComma
              )
            }}
          </span>
        </div>
      </div>
      <div
        v-if="
          !activityDetailsStore.isLoading &&
          activityDetailsTicketsStore.selectedDateOffset !== undefined &&
          activityDetailsStore.activityStaticData?.productCode &&
          activityDetailsStore.activityStaticData?.staticInfo.typeOfHotel
        "
        :id="ActivityDetailsTemplateId.TicketRates"
        class="tickets-container"
      >
        <Ticket
          v-for="(
            ticketRate, index
          ) in activityDetailsTicketsStore.ticketRatesInSelectedDate"
          :key="
            ticketRate.rateId + activityDetailsTicketsStore.selectedDateOffset
          "
          :ticket-rate="ticketRate"
          :product-code="activityDetailsStore.activityStaticData.productCode"
          :date-offset="activityDetailsTicketsStore.selectedDateOffset"
          :initial-rate-tickets="
            recipeStore.getSelectedTicketsForRate(
              ticketRate.rateId,
              activityDetailsTicketsStore.selectedDateOffset,
              activityDetailsStore.activityStaticData?.isAlternative
            )
          "
          :activity-type="
            activityDetailsStore.activityStaticData.staticInfo.typeOfHotel
          "
          @update-selected-status="
            activityDetailsTicketsStore.updateTicketRateValidityStatus(
              $event,
              index
            )
          "
        />
      </div>
    </div>
    <div class="footer-wrapper">
      <ActivityDetailsFooter
        @close="emit('close')"
        @select-tickets="emit('select-tickets')"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.tickets-selector-desktop {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;

  .modal-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
    padding: 0;

    .activities-dates-wrapper {
      position: sticky;
      top: 0;
      background-color: var(--color-white);
      padding: var(--spacing-24) var(--spacing-24) var(--spacing-16);

      .single-date {
        display: flex;
        gap: var(--spacing-12);
        @include font-body-m-light;
        color: var(--color-neutral-70);
      }
    }

    .tickets-container {
      flex: 1;
      overflow-y: auto;
      display: flex;
      flex-direction: column;
      gap: var(--spacing-12);
      padding: 0 var(--spacing-24) var(--spacing-24) var(--spacing-24);
    }
  }

  .footer-wrapper {
    flex-shrink: 0;
    background-color: var(--color-white);
    border-top: 1px solid var(--color-scrollbar);
  }
}
</style>
